import { DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { PenSquare } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Drawer } from '@/components/ui/drawer'
import { cn } from '@/lib/utils'
import { MailCompose } from './components/MailCompose'
import { MailDetail } from './components/MailDetail'
import { MailList } from './components/MailList'
import { MailSettingsDialog } from './components/MailSettingsDialog'
import { MailShortcutsDialog } from './components/MailShortcutsDialog'
import { MailSidebar } from './components/MailSidebar'
import { useMailBox } from './hooks/useMailBox'
import { useMailShortcuts } from './hooks/useMailShortcuts'

/**
 * Posta kutusu. Geniş ekranda klasörler + liste + okuma paneli yan yana;
 * tablette klasörler çekmecede; telefonda liste ve okuma paneli tek tek gösterilir.
 */
export function MailManagementPage() {
  const { t } = useTranslation()
  const box = useMailBox()
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [navOpen, setNavOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }))
  const compose = () => box.openCompose('new')

  useMailShortcuts(box, { compose, help: () => setHelpOpen(true), disabled: settingsOpen || helpOpen || navOpen })

  function onDragEnd(e: DragEndEvent) {
    const active = String(e.active.id)
    const over = e.over ? String(e.over.id) : ''
    if (!active.startsWith('mail:') || !over.startsWith('group:')) return
    box.addMessageToGroup(active.slice(5), over.slice(6))
  }

  const reading = !!box.selected

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="-m-3 flex h-[calc(100dvh-94px)] min-h-[32rem] overflow-hidden rounded-2xl border bg-card shadow-card">
        <MailSidebar className="hidden xl:flex" box={box} onCompose={compose} onOpenSettings={() => setSettingsOpen(true)} />
        <div className={cn('w-full shrink-0 flex-col border-r md:w-[22rem] lg:w-[24rem]', reading ? 'hidden md:flex' : 'flex')}>
          <MailList key={JSON.stringify(box.view)} box={box} onOpenNav={() => setNavOpen(true)} onShortcuts={() => setHelpOpen(true)} />
        </div>
        <div className={cn('min-w-0 flex-1', reading ? 'flex' : 'hidden md:flex')}>
          <MailDetail box={box} />
        </div>
        <MailCompose box={box} />
      </div>

      {!reading && !box.compose.open ? (
        <Button className="fixed bottom-5 right-5 z-30 size-14 rounded-2xl shadow-lg md:hidden" size="icon" onClick={compose} aria-label={t('mail.mailbox.compose')}>
          <PenSquare className="size-5" />
        </Button>
      ) : null}

      <Drawer open={navOpen} onOpenChange={setNavOpen} title={t('mail.mailbox.nav')} className="w-72 bg-card">
        <MailSidebar
          inDrawer
          box={box}
          onCompose={compose}
          onNavigate={() => setNavOpen(false)}
          onOpenSettings={() => {
            setNavOpen(false)
            setSettingsOpen(true)
          }}
        />
      </Drawer>
      <MailSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} settings={box.settings} onSave={box.saveSettings} />
      <MailShortcutsDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </DndContext>
  )
}
